import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/logo";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Reset password — MG Content Labs" },
      { name: "description", content: "Choose a new password for your MG Content Labs account." },
      { property: "og:title", content: "Reset password — MG Content Labs" },
      { property: "og:description", content: "Choose a new password for your MG Content Labs account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // The recovery link lands here with a token in the URL hash; Supabase
    // exchanges it for a session automatically.
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("access_token")) {
      setReady(true);
      return;
    }
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) return setError(updateError.message);
    setDone(true);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-muted/40 px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-brand-deep/10 bg-white p-8 shadow-xl">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        <h1 className="text-center text-2xl font-semibold text-brand-deep">Set a new password</h1>

        {done ? (
          <>
            <p className="mt-4 rounded-md border border-brand-accent/30 bg-brand-accent/5 px-3 py-2 text-center text-sm text-brand-deep">
              Your password has been updated. You can now sign in with it.
            </p>
            <Link
              to="/auth"
              search={{ next: "/" }}
              className="mt-6 block w-full rounded-full bg-brand-accent px-5 py-2.5 text-center text-sm font-semibold text-white"
            >
              Go to sign in
            </Link>
          </>
        ) : ready ? (
          <>
            <p className="mt-2 text-center text-sm text-brand-deep/60">
              Enter and confirm your new password below.
            </p>
            {error && (
              <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                autoComplete="new-password"
                className="w-full rounded-lg border border-brand-deep/15 bg-white px-4 py-2.5 text-sm text-brand-deep focus:border-brand-accent focus:outline-none"
              />
              <input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                className="w-full rounded-lg border border-brand-deep/15 bg-white px-4 py-2.5 text-sm text-brand-deep focus:border-brand-accent focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
              >
                Update password
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="mt-4 text-center text-sm text-brand-deep/60">
              This reset link is invalid or has expired. Request a fresh one from the sign-in page.
            </p>
            <Link
              to="/auth"
              search={{ next: "/" }}
              className="mt-6 block w-full rounded-full border border-brand-deep/15 bg-white px-5 py-2.5 text-center text-sm font-semibold text-brand-deep hover:bg-brand-muted/60"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
